import React, { useEffect, useMemo, useState } from 'react'
import PluviometryAction from '../../../actions/PluviometryAction'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import Bar from '../../../../components/echart/series/Bar'
import i18n from 'simple-react-i18n'
import Axis from '../../../../components/echart/Axis'
import {
    exportExcelIcon,
    exportPictureIcon,
    getAxisIntervalFormatter,
    toEchartsData,
} from '../../../../components/echart/EChartUtils'
import { maxBy, minBy, partition, round } from 'lodash'
import EChart from '../../../../components/echart/EChart'
import MessageCard from '../../../../components/card/MessageCard'
import ProgressCard from '../../../../components/card/ProgressCard'
import { getFullDate, getMonthYear, getYear, getYearDate } from '../../../../utils/DateUtil'
import PropTypes from 'prop-types'
import PluviometerDto from '../../../dto/PluviometerDto'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { DEFROUNDVALUE } from 'utils/NumberUtil'
import { getExportFormat } from 'utils/StringUtil'
import { DATATYPE_RAIN } from 'utils/PluviometryUtils'

const MONTHS = 'MONTHS'
const YEARS = 'YEARS'
const NORMALE_MONTH = 'NORMALE_TOZERO_MONTH'
const NORMALE_YEAR = 'NORMALE_TOZERO_YEAR'
const NormalsGraphPluvio = ({
    pluviometer,
    typeGroupFunc = MONTHS,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const maxDate = moment().valueOf()
    const height = 400

    const dispatch = useDispatch()

    const groupFuncSelection = () => {
        switch (typeGroupFunc) {
            case MONTHS:
                return NORMALE_MONTH
            case YEARS :
                return NORMALE_YEAR
            default:
                return NORMALE_MONTH
        }
    }

    useEffect(()=> {
        setDataLoaded(false)
        const inputsMonths = {
            stationId: pluviometer.id,
            dataType: 1,
            groupFunc: groupFuncSelection(),
            chartMode: true,
        }
        dispatch(PluviometryAction.loadPluvioChronicMeasures([inputsMonths], p => {
            setProgress(p)
        })).then(() => {
            setDataLoaded(true)
        })
    }, [typeGroupFunc])

    const {
        pluvioMeasures,
        pluviometerStatistics,
        pluviometryDataTypes,
    } = useSelector(store => ({
        pluvioMeasures: store.PluviometryReducer.pluvioMeasures,
        pluviometerStatistics: store.PluviometryReducer.pluviometerStatistics,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
    }), shallowEqual)

    const unit = pluviometerStatistics.find(p => p.typeId === DATATYPE_RAIN)?.unit
    const unitLabel = unit ? `[${unit}]` : ''

    const getBarNormals = (data, color) => {
        return Bar({
            name: i18n.rain,
            barWidth: typeGroupFunc === YEARS ? 4 : 2,
            data: toEchartsData(data),
            areaStyle: {},
            color,
        })
    }

    const tooltipDate = (params) => {
        switch (typeGroupFunc) {
            case YEARS :
                return getYearDate(params)
            case MONTHS :
                return getMonthYear(params)
            default :
                return getFullDate(params)
        }
    }
    const getTooltip = () => {
        return {
            trigger: 'axis',
            formatter: params => {
                const paramsFormat = params[0]
                const value = paramsFormat.data.value[1]
                const date = tooltipDate(params[0].data.value[0])
                const { marker, seriesName } = paramsFormat
                const result = `${marker} ${seriesName} : ${round(value, 2)} ${i18n.milimetersSymbol}`
                return `${date}<br />${result}`
            },
        }
    }

    const roundValue = pluviometryDataTypes.find(pdt => pdt.id === DATATYPE_RAIN)?.numberDecimal || DEFROUNDVALUE
    const pluvioMeasuresFormat = pluvioMeasures[0]?.measures
    const pluvioMeasuresFormatExport = useMemo(() => {
        return pluvioMeasuresFormat.filter(d => d.date >= pluvioMeasuresFormat[0].date && d.date <= maxDate).map(d => ({
            stationCode: { value: pluviometer.code },
            stationName: { value: pluviometer.name },
            date: { value: getFullDate(d.date), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
            value: { value: round(d.value, 2), format: getExportFormat(roundValue), cellType: 'number' },
            type: { value: `${typeGroupFunc === MONTHS ? i18n.normalMonths : i18n.normalYears}` },
            headers: ['stationCode', 'stationName', 'date', 'value', 'type'],
        }))
    }, [pluvioMeasuresFormat, maxDate, pluviometer.code, pluviometer.name, typeGroupFunc])

    const [tabDataPositive, tabDataNegative] = partition(pluvioMeasuresFormat, m => m.value > 0)
    const series = [getBarNormals(tabDataPositive, 'blue'), getBarNormals(tabDataNegative, 'red')]

    const seriesChart = series.filter(s => !!s).flat()
    const axisLabelObj = getAxisIntervalFormatter(moment(), 0)

    const options = {
        title: `[${pluviometer.code}] - ${pluviometer.name} `,
        series: seriesChart,
        tooltip: getTooltip(),
        grid: {
            top: 45,
            right: '10%',
            height: height - 50,
            left: 120,
        },
        xAxis: [Axis({
            type: 'time',
            position: 'bottom',
            min: pluvioMeasuresFormat[0].date,
            max: maxDate,
            axisLine: { show: true },
            axisTick: { show: true },
            showSplitLine: true,
            axisLabel: { show: true, formatter: axisLabelObj.formatter },
        })],
        yAxis: [Axis({
            type: 'value',
            nameLocation: 'middle',
            name: `${i18n.comparisonNormal} ${unitLabel}`,
            nameGap: 40,
            showSplitLine: true,
            min: undefined,
        })],
        axisPointer: {
            link: { xAxisIndex: 'all' },
        },
        setDataZoom: true,
        toolbox: {
            show: true,
            feature: {
                restore: { title: i18n.restore },
                saveAsImage: { title: i18n.export, icon: exportPictureIcon },
                myToolExport: {
                    show: true,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        exportFile({
                            data: pluvioMeasuresFormatExport,
                            exportType: 'xlsx',
                            titleFile: i18n.overview,
                        })
                    },
                },
            },
            right: 65,
        },
        height,
    }

    if (!pluvioMeasuresFormat.length) {
        return (
            <div style={{ marginTop: 20 }}>
                <MessageCard>
                    { i18n.noData }
                </MessageCard>
            </div>
        )
    }
    const maxMeasure = maxBy(pluvioMeasuresFormat, 'value')
    const minMeasure = minBy(pluvioMeasuresFormat, 'value')
    const minV = minMeasure.value
    const maxV = maxMeasure.value
    const minVdate = minMeasure.date
    const maxVdate = maxMeasure.date
    return (
        <div className='col s12 row no-margin'>
            <div style={{ marginTop: '15px' }}>
                { !pluvioMeasuresFormat.length && (
                    <div style={{ marginTop: 20 }}>
                        <MessageCard>
                            { i18n.noData }
                        </MessageCard>
                    </div>
                )}
                { !dataLoaded && (
                    <ProgressCard progress={progress} />
                )}
                { dataLoaded && pluvioMeasuresFormat.length && (
                    <>
                        <EChart options={options} id='graphNormalsPluvioChart' />
                        <StyledFieldSet>
                            <StyledLegend>{i18n.statistics}</StyledLegend>
                            <div style={{ padding: '5px' }}>
                                <div>{ typeGroupFunc === MONTHS ? i18n.monthlyMax : i18n.yearsMax } : {maxV} {i18n.milimetersSymbol} - {getYear(maxVdate)}</div>
                                <div className='divider'/>
                                <div>{ typeGroupFunc === MONTHS ? i18n.monthlyMin : i18n.yearsMax } : {minV} {i18n.milimetersSymbol} - {getYear(minVdate)}</div>
                            </div>
                        </StyledFieldSet>
                    </>
                )}

            </div>
        </div>
    )
}

NormalsGraphPluvio.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    typeGroupFunc: PropTypes.string,
}

export default NormalsGraphPluvio